import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    bandsForSlider,
    fisheyeLayout,
    orderForSlider,
    rowCentre,
    type SliderClass,
} from '../lib/classification-fisheye'

/** Below this a row is a sliver of colour, not a line of text: the name would be too small to read. */
const LABEL_MIN_H = 9
/** The strip down the left edge that names each category band. */
const STRIP_W = 22
/** A category name runs down its strip only where the band is tall enough to hold it. */
const STRIP_LABEL_MIN_H = 56
/**
 * Text on a classification colour. The palette is pastel in both themes — it is the same hex the
 * canvas bands use — so the ink on it is dark in both themes too, not the theme's foreground.
 */
const INK = '#27272a'
/** Below the cut a band keeps its hue but steps back: recovered and not-recovered must differ at a glance. */
const FADED = 0.28
/** The column's height in "fill" mode until it has been measured (and where it cannot be, e.g. jsdom). */
const FILL_FALLBACK = 300

/**
 * "Recover from classification" as a vertical slider in stack order — the highest classification
 * at the top, the lowest at the bottom — magnified around the pointer, and drawn as the stack of
 * CATEGORY BANDS the canvas already shows (option C, Andrew, 2026-09-21).
 *
 * Each category is a band in its own colour with its name down a strip on the left, so even the
 * rows the magnification has shrunk to slivers still say what part of the stack they are; the
 * first cut of this slider drew those as bare lines, and the top of the column read as empty.
 * The choice means "this classification and everything above it is recovered", so the cut is a
 * line under the chosen row with the bands above it at full colour and the ones below faded.
 *
 * At rest the magnification sits on the current choice; move the pointer over the column and it
 * follows. See lib/classification-fisheye for why the row under the pointer stays under the
 * pointer — a click always lands on the row that was highlighted.
 *
 * It is a real slider for keyboard and assistive tech: role="slider", vertical, Up and Down step
 * one classification at a time, Home and End jump to the top and bottom, Page Up and Page Down
 * move five.
 */
export function ClassificationSlider({ classes, value, onChange, height = 300, labelledBy, disabled = false }: {
    classes: SliderClass[]
    /** The chosen band's DisplayRank. */
    value: number | null
    onChange: (rank: number) => void
    /**
     * A pixel height, or "fill" to take whatever height the parent gives it. The magnification is
     * computed in pixels, so "fill" measures the column and re-lays it out when the parent resizes
     * — in the two-column Request Recovery dialog the column IS the height of the dialog body.
     */
    height?: number | 'fill'
    labelledBy?: string
    disabled?: boolean
}) {
    const { t } = useTranslation()
    const rows = useMemo(() => orderForSlider(classes), [classes])
    const bands = useMemo(() => bandsForSlider(rows), [rows])
    const count = rows.length
    // One step per distinct band: two classifications sharing a DisplayRank are the same cut.
    const ranks = useMemo(() => [...new Set(rows.map((r) => Number(r.DisplayRank)))], [rows])

    const columnRef = useRef<HTMLDivElement>(null)
    const frame = useRef<number | null>(null)
    const [pointer, setPointer] = useState<number | null>(null)

    const fill = height === 'fill'
    const [measured, setMeasured] = useState<number | null>(null)
    // Keyed on whether the column EXISTS, not only on the mode: with no classifications yet (the
    // dialog opens before they load) this renders nothing, so the first run finds no column — and
    // keyed on `fill` alone it would never run again, leaving the slider at the fallback height.
    const hasRows = count > 0
    useLayoutEffect(() => {
        const el = columnRef.current
        if (!fill || !el || typeof ResizeObserver === 'undefined') return
        const ro = new ResizeObserver(([entry]) => {
            const h = Math.round(entry.contentRect.height)
            if (h > 0) setMeasured(h)
        })
        ro.observe(el)
        return () => ro.disconnect()
    }, [fill, hasRows])
    const H = fill ? (measured ?? FILL_FALLBACK) : height

    const selectedIndex = value == null ? -1 : rows.findIndex((r) => Number(r.DisplayRank) === value)
    // The row a click would choose: the one the pointer is over. Andrew, 2026-09-25: "as we're
    // scrolling [through the classifications] it needs to show which classification we are
    // selecting on". The magnification followed the pointer but nothing said which row it was on,
    // and near the ends the rows are too thin to carry their names at all.
    // At rest, magnify the choice; with the pointer over the column, magnify under the pointer.
    const focus = pointer ?? (selectedIndex >= 0 ? rowCentre(count, H, selectedIndex) : H / 2)
    const layout = useMemo(() => fisheyeLayout(count, H, focus), [count, H, focus])
    const hoveredIndex = pointer == null || disabled
        ? -1
        : layout.findIndex((r) => pointer >= r.top && pointer < r.top + r.height)

    const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        const box = columnRef.current?.getBoundingClientRect()
        if (!box) return
        const y = e.clientY - box.top
        if (frame.current != null) cancelAnimationFrame(frame.current)
        frame.current = requestAnimationFrame(() => setPointer(y))
    }, [])
    const onPointerLeave = useCallback(() => {
        if (frame.current != null) cancelAnimationFrame(frame.current)
        setPointer(null)
    }, [])

    const step = (delta: number) => {
        if (!ranks.length) return
        const at = value == null ? -1 : ranks.indexOf(value)
        const start = at < 0 ? (delta > 0 ? -1 : ranks.length) : at
        const next = Math.min(ranks.length - 1, Math.max(0, start + delta))
        onChange(ranks[next])
    }
    const onKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) return
        // Up the column is up the stack: a HIGHER classification, which is an earlier index.
        const moves: Record<string, number | 'top' | 'bottom'> = {
            ArrowUp: -1, ArrowRight: -1, ArrowDown: 1, ArrowLeft: 1,
            PageUp: -5, PageDown: 5, Home: 'top', End: 'bottom',
        }
        const m = moves[e.key]
        if (m === undefined) return
        e.preventDefault()
        if (m === 'top') onChange(ranks[0])
        else if (m === 'bottom') onChange(ranks[ranks.length - 1])
        else step(m)
    }

    if (!count) return null
    const selected = selectedIndex >= 0 ? rows[selectedIndex] : null
    const hovered = hoveredIndex >= 0 ? rows[hoveredIndex] : null
    const hoverBox = hoveredIndex >= 0 ? layout[hoveredIndex] : null
    // A different cut from the chosen one: preview where the line would go.
    const previewDiffers = !!hovered && Number(hovered.DisplayRank) !== value
    const readout = hovered ?? selected
    const cut = selectedIndex >= 0 ? layout[selectedIndex] : null
    const bandOf = (i: number) => bands.find((b) => i >= b.start && i <= b.end)!
    // The cut label sits under the line, or above it where the line is at the very bottom.
    const cutY = cut ? cut.top + cut.height : 0
    const labelBelow = cutY < H - 18

    return (
        <div className={`flex w-full flex-col gap-1 ${fill ? 'h-full min-h-0' : 'max-w-md'}`}>
            {/* What is chosen, or — while the pointer is over the column — what a click would choose.
                Always one line, so the column below does not jump as it changes. */}
            <div className="flex items-baseline justify-between gap-2 pl-1" data-testid="slider-readout" aria-live="polite">
                <span className="min-w-0 truncate text-xs">
                    {readout ? (
                        <>
                            <span className="text-muted-foreground">
                                {hovered ? t('objects.sliderSelecting', 'Selecting') : t('objects.sliderSelected', 'Selected')}:
                            </span>{' '}
                            <span className={hovered ? 'font-semibold text-primary' : 'font-semibold'}>{readout.Name}</span>
                            {readout.Category && <span className="text-muted-foreground"> · {readout.Category}</span>}
                        </>
                    ) : (
                        <span className="text-muted-foreground">{t('objects.sliderNoneSelected', 'Choose a classification')}</span>
                    )}
                </span>
                <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('objects.classificationHighest', 'Highest')}
                </span>
            </div>
            <div
                ref={columnRef}
                role="slider"
                tabIndex={disabled ? -1 : 0}
                aria-orientation="vertical"
                aria-labelledby={labelledBy}
                aria-valuemin={ranks[ranks.length - 1]}
                aria-valuemax={ranks[0]}
                aria-valuenow={value ?? undefined}
                aria-valuetext={selected?.Name}
                aria-disabled={disabled || undefined}
                onKeyDown={onKeyDown}
                onPointerMove={disabled ? undefined : onPointerMove}
                onPointerLeave={onPointerLeave}
                className={`relative select-none overflow-hidden rounded-md border bg-background outline-none focus-visible:ring-2 focus-visible:ring-ring ${fill ? 'min-h-0 flex-1' : ''}`}
                style={fill ? undefined : { height: H }}
            >
                {rows.map((row, i) => {
                    const { top, height: h } = layout[i]
                    const rank = Number(row.DisplayRank)
                    const recovered = value != null && rank >= value
                    const isCut = i === selectedIndex
                    const isHovered = i === hoveredIndex
                    const colour = bandOf(i).colour
                    const fontSize = Math.min(13, Math.max(8, h * 0.55))
                    return (
                        <button
                            key={row.id}
                            type="button"
                            tabIndex={-1}
                            disabled={disabled}
                            data-rank={rank}
                            data-recovered={recovered || undefined}
                            data-hovered={isHovered || undefined}
                            onClick={() => onChange(rank)}
                            className={`absolute right-0 flex items-center text-left ${isCut || isHovered ? 'font-semibold' : ''}`}
                            style={{ left: STRIP_W, top, height: h, paddingLeft: 8 }}
                            title={`${row.Name}${row.Category ? ` · ${row.Category}` : ''}`}
                        >
                            {/* The band's colour, stepped back below the cut. A background layer, so
                                the fade never touches the text's own contrast. */}
                            <span aria-hidden className="absolute inset-0"
                                  style={{ background: colour || 'hsl(var(--muted))', opacity: recovered ? 1 : FADED }} />
                            {h >= LABEL_MIN_H && (
                                <span
                                    className={`relative truncate ${recovered ? '' : 'text-muted-foreground'}`}
                                    style={{ fontSize, lineHeight: 1.1, color: recovered && colour ? INK : undefined }}
                                >
                                    {row.Name}
                                </span>
                            )}
                        </button>
                    )
                })}

                {/* The category strips, each running the height of its band. */}
                {bands.map((band) => {
                    const top = layout[band.start].top
                    const h = layout[band.end].top + layout[band.end].height - top
                    return (
                        <div
                            key={`${band.category}-${band.start}`}
                            data-band={band.category}
                            aria-hidden
                            className="pointer-events-none absolute left-0 overflow-hidden border-b border-background"
                            style={{ top, height: h, width: STRIP_W, background: band.colour || 'hsl(var(--muted))' }}
                        >
                            {h >= STRIP_LABEL_MIN_H && band.category && (
                                <span
                                    className="absolute left-1/2 top-1/2 whitespace-nowrap text-[10px] font-semibold uppercase tracking-wide"
                                    style={{ transform: 'translate(-50%, -50%) rotate(-90deg)', color: band.colour ? INK : undefined }}
                                >
                                    {band.category}
                                </span>
                            )}
                        </div>
                    )
                })}

                {/* The row under the pointer, outlined, with a dashed line where the cut would move to.
                    No separate name label: the magnification always makes the row under the pointer
                    the largest, so it carries its own name, and the readout above repeats it. */}
                {hovered && hoverBox && (
                    <>
                        <div aria-hidden data-hover-outline className="pointer-events-none absolute right-0 rounded-sm ring-2 ring-inset ring-primary"
                             style={{ left: STRIP_W, top: hoverBox.top, height: Math.max(hoverBox.height, 2) }} />
                        {previewDiffers && (
                            <div data-preview-cut aria-hidden className="pointer-events-none absolute right-0 border-t-2 border-dashed border-primary/70"
                                 style={{ left: STRIP_W, top: hoverBox.top + hoverBox.height - 1 }} />
                        )}
                    </>
                )}

                {/* The cut: everything above this line is recovered. */}
                {cut && (
                    <>
                        <div data-cut aria-hidden className="pointer-events-none absolute right-0 bg-primary"
                             style={{ left: STRIP_W, top: cutY - 1, height: 2 }} />
                        <span aria-hidden className="pointer-events-none absolute right-2 rounded bg-background/90 px-1 text-[10px] font-medium text-primary"
                              style={labelBelow ? { top: cutY + 2 } : { top: cutY - 18 }}>
                            {t('objects.recoveredAboveLine', 'Recovered above the line')}
                        </span>
                    </>
                )}
            </div>
            <span className="pl-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t('objects.classificationLowest', 'Lowest')}
            </span>
        </div>
    )
}
